/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  ACTIONS,
  BannerViewParams,
  BannerTapParams,
  CHINA_DISCLAIMER_VIEWED,
  ChinaDisclaimerViewedParams,
  CouponClaimParams,
  CouponViewedParams,
  EVENT_NAMES,
  FEATURED_DESTINATION_CLICKED,
  FEATURED_DESTINATION_VIEWED,
  FeaturedDestinationClickedParams,
  FeaturedDestinationViewedParams,
  HOTEL_DEALS_CLICKED,
  HOTEL_DEALS_VIEWED,
  HotelDealsClickedParams,
  HotelDealsViewedParams,
  HotelDealsViewItem,
  HOTEL_BANNER,
  LABELS,
  LAST_SEARCHED_CLICKED,
  LAST_SEARCHED_VIEWED,
  LastSearchedClickedParams,
  LastSearchedViewedParams,
  LINE_OF_BUSINESS,
  PROVIDER,
  SCREEN_NAME,
  SearchButtonParams,
} from './searchWidget.meta';
import type { Suggestions } from '../../types';

/**
 * Hotel Banner Analytics
 */

/**
 * Build analytics parameters for hotel banner view event
 * @param params - Banner view parameters (bannerName, position)
 * @returns Analytics parameters object
 */
export const buildHotelBannerViewAnalytics = (
  params: BannerViewParams,
): Record<string, string | number> => {
  const label = `${params.bannerName}|${params.position}`;

  return {
    action: ACTIONS.VIEW,
    category: HOTEL_BANNER.CATEGORY,
    label,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
    screenName: SCREEN_NAME.hotelSearchPage,
  };
};

export const buildCouponViewedAnalytics = (
  params: CouponViewedParams,
): Record<string, string | number> => {
  const label = `${params.couponId}|${params.position}`;

  return {
    category: 'Hotels SA|hotelsearchwidget',
    action: EVENT_NAMES.COUPONS_VIEWED,
    label,
    screenName: SCREEN_NAME.hotelSearchPage,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
  };
};

/**
 * Build analytics parameters for hotel banner tap event
 * @param params - Banner tap parameters (bannerName, position)
 * @returns Analytics parameters object
 */
export const buildHotelBannerTapAnalytics = (
  params: BannerTapParams,
): Record<string, string | number> => {
  const label = `${params.bannerName}|${params.position}`;

  return {
    action: ACTIONS.TAP,
    category: HOTEL_BANNER.CATEGORY,
    label,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
    screenName: SCREEN_NAME.hotelSearchPage,
  };
};

/**
 * Search Widget Analytics
 */

/**
 * Build analytics parameters for search widget view event
 * @returns Analytics parameters object
 */
export const buildSearchWidgetViewAnalytics = (): Record<
  string,
  string | number
> => {
  return {
    action: EVENT_NAMES.SEARCH_WIDGET_VIEWED,
    category: 'Hotels SA|USW',
    label: 'Hotel Widget Exposed',
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
    screenName: SCREEN_NAME.hotelSearchPage,
  };
};

/**
 * Build analytics parameters for smart fill input event
 * @param userQuery - The user's search input
 * @param provider - The search provider (TypeSense or ElasticSearch)
 * @returns Analytics parameters object
 */
export const buildSmartFillInputAnalytics = (
  userQuery: string,
  provider: string,
): Record<string, string | number> => {
  const label = `${userQuery}|${provider}`;

  return {
    action: EVENT_NAMES.SMART_FILL_INPUT,
    category: 'Hotels SA|hotelsearchwidget',
    label,
    screenName: SCREEN_NAME.hotelSearchPage,
  };
};

/**
 * Build analytics parameters for suggestion click event
 * @param stationName - The selected station name
 * @param position - The position of the suggestion in the list
 * @returns Analytics parameters object
 */
export const buildSuggestionClickAnalytics = (
  stationName: string,
  position: number,
): Record<string, string | number> => {
  const label = `${stationName}|${position}`;

  return {
    action: ACTIONS.TAP,
    category: 'Hotels SA|hotelsearchwidget',
    label,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
    screenName: SCREEN_NAME.hotelSearchPage,
  };
};

/**
 * Build analytics parameters for popular search click event
 */
export const buildPopularSearchClickAnalytics = (
  stationName: string,
  position: number,
  searchterm: string | null,
  destinationcategory: string,
  location_id: string,
): Record<string, any> => {
  return {
    action: ACTIONS.TAP,
    category: 'Hotels SA|hotelsearchwidget|popularsearches',
    label: `${stationName}|${position}`,
    searchterm,
    destinationcategory,
    location_id,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
    screenName: SCREEN_NAME.hotelSearchPage,
  };
};

/**
 * Build analytics parameters for popular search viewed event
 */
export const buildPopularSearchViewedAnalytics = (
  items: Array<{
    item_id: string;
    item_name: string;
    item_category: string;
    index: number;
  }>,
): Record<string, any> => {
  return {
    action: 'view_item_list',
    screenName: SCREEN_NAME.hotelSearchPage,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
    items: items.map((item) => ({
      item_list_name: 'hotelWidget|popularsearches',
      item_id: item.item_id,
      item_name: item.item_name,
      price: 0,
      item_brand: LINE_OF_BUSINESS.hotelsSA,
      item_category: item.item_category,
      item_variant: '',
      index: item.index,
    })),
  };
};

/**
 * Build analytics parameters for no results event
 * @param userQuery - The user's search query that returned no results
 * @returns Analytics parameters object
 */
export const buildNoResultsAnalytics = (
  userQuery: string,
): Record<string, string | number> => {
  return {
    action: 'errors',
    category: 'Hotels SA|hotelsearchwidget',
    label: LABELS.NO_RESULTS,
    value: userQuery,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
    screenName: SCREEN_NAME.hotelSearchPage,
  };
};

/**
 * Build analytics parameters for search button click event
 * @param params - Search button click parameters
 * @returns Analytics parameters object
 */
export const buildSearchButtonClickAnalytics = (
  params: SearchButtonParams,
): Record<string, string | number> => {
  return {
    action: EVENT_NAMES.SEARCH_BUTTON_CLICKED,
    destination: params.destination,
    destinationcategory: params.destinationcategory,
    destinationcountry: params.destinationcountry,
    checkindate: params.checkindate,
    checkoutdate: params.checkoutdate,
    lengthofstay: params.lengthofstay,
    rooms: params.rooms,
    totalpax: params.totalpax,
    adult: params.adult,
    child: params.child,
    infant: params.infant,
    screenName: params.isMap
      ? SCREEN_NAME.MapView
      : SCREEN_NAME.hotelSearchPage,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
  };
};

/**
 * Build analytics parameters for coupon claim event
 * @param params - Coupon claim parameters
 * @returns Analytics parameters object
 */
export const buildCouponClaimAnalytics = (
  params: CouponClaimParams,
): Record<string, string | number> => {
  const label = `${params.couponId}|${params.position}`;

  return {
    category: 'Hotels SA|hotelsearchwidget',
    action: EVENT_NAMES.COUPONS_CLAIMED,
    label,
    screenName: SCREEN_NAME.hotelSearchPage,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
  };
};

/**
 * Build analytics parameters for last searched viewed event
 * @param params - Last searched viewed parameters
 * @returns Analytics parameters object
 */
export const buildLastSearchedViewedAnalytics = (
  params: LastSearchedViewedParams,
): Record<string, string | number> => {
  const label = params.destinations
    .map((dest) => `${dest.destinationId}|${dest.position}`)
    .join(',');

  return {
    category: LAST_SEARCHED_VIEWED.CATEGORY,
    action: LAST_SEARCHED_VIEWED.ACTION,
    label,
    screenName: SCREEN_NAME.hotelSearchPage,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
  };
};

/**
 * Build analytics parameters for last searched clicked event
 * @param params - Last searched clicked parameters
 * @returns Analytics parameters object
 */
export const buildLastSearchedClickedAnalytics = (
  params: LastSearchedClickedParams,
): Record<string, string | number> => {
  const label = `${params.destinationId}|${params.position}`;

  return {
    category: LAST_SEARCHED_CLICKED.CATEGORY,
    action: LAST_SEARCHED_CLICKED.ACTION,
    label,
    screenName: SCREEN_NAME.hotelSearchPage,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
  };
};

/**
 * Build analytics parameters for featured destination viewed event
 * @param params - Featured destination viewed parameters
 * @returns Analytics parameters object
 */
export const buildFeaturedDestinationViewedAnalytics = (
  params: FeaturedDestinationViewedParams,
): Record<string, string | number> => {
  const label = params.destinationCodes.join('|');

  return {
    action: EVENT_NAMES.FEATURED_DESTINATION_VIEWED,
    category: FEATURED_DESTINATION_VIEWED.CATEGORY,
    label,
    screenName: SCREEN_NAME.hotelSearchPage,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
  };
};

/**
 * Build analytics parameters for featured destination clicked event
 * @param params - Featured destination clicked parameters
 * @returns Analytics parameters object
 */
export const buildFeaturedDestinationClickedAnalytics = (
  params: FeaturedDestinationClickedParams,
): Record<string, string | number> => {
  return {
    action: EVENT_NAMES.FEATURED_DESTINATION_CLICKED,
    category: FEATURED_DESTINATION_CLICKED.CATEGORY,
    label: params.destinationId,
    screenName: SCREEN_NAME.hotelSearchPage,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
  };
};

/**
 * Build analytics parameters for hotel deals viewed event
 * @param params - Hotel deals viewed parameters
 * @returns Analytics parameters object
 */
export const buildHotelDealsViewedAnalytics = (
  params: HotelDealsViewedParams,
): Record<string, string | number | HotelDealsViewItem[]> => {
  const category = `${HOTEL_DEALS_VIEWED.CATEGORY_PREFIX}|${params.recommendationTitle}`;

  return {
    action: EVENT_NAMES.HOTEL_DEALS_VIEWED,
    category,
    lineofbusiness: HOTEL_DEALS_VIEWED.LINE_OF_BUSINESS,
    screenName: HOTEL_DEALS_VIEWED.SCREEN_NAME,
    currency: params.currency,
    item_list_name: params.recommendationTitle,
    items: params.items,
  };
};

/**
 * Build analytics parameters for hotel deals clicked event
 * @param params - Hotel deals clicked parameters
 * @returns Analytics parameters object
 */
export const buildHotelDealsClickedAnalytics = (
  params: HotelDealsClickedParams,
): Record<string, string | number> => {
  const category = `${HOTEL_DEALS_CLICKED.CATEGORY_PREFIX}|${params.recommendationTitle}`;

  return {
    action: EVENT_NAMES.HOTEL_DEALS_CLICKED,
    category,
    lineofbusiness: HOTEL_DEALS_CLICKED.LINE_OF_BUSINESS,
    screenName: 'Hotel Search Page V2',
    recommendationtype: params.recommendationType,
    hotelid: params.hotelId,
    hotelname: params.hotelName,
    destination: params.destination,
    destinationcategory: params.destinationCategory,
    destinationcountry: params.destinationCountry,
    checkindate: params.checkInDate,
    checkoutdate: params.checkOutDate,
    lengthofstay: params.lengthOfStay,
    rooms: params.rooms,
    currency: params.currency,
    price: params.price,
    strikethroughprice: params.strikeThroughPrice,
    totalpax: params.totalPax,
    adult: params.adult,
    child: params.child,
    infant: params.infant,
  };
};

/**
 * Build analytics parameters for China disclaimer viewed event
 * @param params - China disclaimer viewed parameters
 * @returns Analytics parameters object
 */
export const buildChinaDisclaimerViewedAnalytics = (
  params: ChinaDisclaimerViewedParams,
): Record<string, string | number> => {
  return {
    action: ACTIONS.VIEW,
    category: CHINA_DISCLAIMER_VIEWED.CATEGORY,
    label: params.disclaimerMessage,
    lineofbusiness: CHINA_DISCLAIMER_VIEWED.LINE_OF_BUSINESS,
    screenName: CHINA_DISCLAIMER_VIEWED.SCREEN_NAME,
  };
};

/**
 * Build analytics parameters for smart fill results event
 * @param suggestions - Array of suggestions from smart fill response
 * @returns Analytics parameters object
 */
export const buildSmartFillResultsAnalytics = (
  suggestions: Suggestions[],
): Record<string, string | number | any> => {
  const items = suggestions.map((suggestion, index) => ({
    item_list_name: `hotelWidget|smart fill search results|${PROVIDER.TYPESENSE}`,
    item_id: suggestion.id,
    item_name: suggestion.name,
    price: 0.0,
    item_brand: LINE_OF_BUSINESS.hotelsSA,
    item_category: suggestion.type,
    item_variant: '',
    index: index + 1,
  }));

  return {
    action: EVENT_NAMES.SMART_FILL_RESULTS,
    items,
    lineofbusiness: LINE_OF_BUSINESS.hotelsSA,
    screenName: SCREEN_NAME.hotelSearchPage,
  };
};
